import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import {
  type Nil,
  PermissionArea,
  PuiTextField,
  useFields,
} from '@pbt/pbt-ui-components'

import SoapWidget from '~/components/common/SoapWidget'
import FeatureToggle from '~/constants/featureToggle'
import { updateAppointmentNotes } from '~/store/actions/timetable'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getTimetableIsLoading,
  getTimetableIsUpdatingNotes,
} from '~/store/reducers/timetable'
import useFieldsChanged from '~/utils/useFieldsChanged'

import NotesTemplateInput from '../../template-inputs/NotesTemplateInput'

export interface ReasonForVisitWidgetUnlockedProps {
  appointmentId: string
  appointmentNotes: string | Nil
}

const useStyles = makeStyles(
  (theme) => ({
    content: {
      padding: theme.spacing(1, 5, 1, 2),
    },
  }),
  { name: 'ReasonForVisitWidgetUnlocked' },
)

const ReasonForVisitWidgetUnlocked = ({
  appointmentId,
  appointmentNotes,
}: ReasonForVisitWidgetUnlockedProps) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation('Common')

  const appointmentPermissions = useSelector(
    getCRUDByArea(PermissionArea.EVENT_APPOINTMENT),
  )
  const isLoading = useSelector(getTimetableIsLoading)
  const isUpdatingNotes = useSelector(getTimetableIsUpdatingNotes)
  const isRichTextNotesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_DIALOG_ADDITIONAL_FIELDS),
  )

  const [hasFocusedField, setHasFocusedField] = useState(false)

  const { fields, reset } = useFields([
    {
      name: 'notes',
      label: t('Common:REASON_FOR_VISIT'),
      initialValue: appointmentNotes || '',
    },
  ])

  const { notes } = fields

  useEffect(() => {
    if (!hasFocusedField) {
      reset()
    }
  }, [appointmentId])

  useFieldsChanged(() => {
    dispatch(
      updateAppointmentNotes({
        id: appointmentId,
        notes: notes.value,
        debounced: true,
      }),
    )
  }, fields)

  return (
    <SoapWidget
      classes={{ content: classes.content }}
      id="reason-for-visit-widget"
      title={notes.label}
    >
      {isRichTextNotesEnabled ? (
        <NotesTemplateInput
          isSoap
          singleLine
          disabled={!appointmentPermissions.update}
          field={notes}
          maxHeight={150}
          minHeight={30}
        />
      ) : (
        <PuiTextField
          multiline
          disabled={!appointmentPermissions.update}
          field={notes}
          id="primary-complaint-input"
          isLoading={isLoading || isUpdatingNotes}
          maxRows={6}
          minRows={1}
          onBlur={() => {
            setHasFocusedField(false)
          }}
          onFocus={() => {
            setHasFocusedField(true)
          }}
        />
      )}
    </SoapWidget>
  )
}

export default ReasonForVisitWidgetUnlocked
